import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CloudUpload, CloudDownload, Globe, ChevronRight, AppWindow } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { getNetworkActivity } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';
import dayjs from 'dayjs';
import { PageHeader } from '../components/ui/page-header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { cn } from '../lib/utils';

function NetworkTrafficPage() {
  const { workerId } = useParams();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filterParams, setFilterParams] = useState({ startDate: null, endDate: null, hours: null });
  const [expandedApps, setExpandedApps] = useState({});
  const itemsPerPage = 500;

  const toggleApp = (key) =>
    setExpandedApps((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    loadData();
  }, [workerId, page, filterParams]);

  const loadData = async () => {
    try {
      setLoading(true);
      const hours = filterParams.startDate ? null : filterParams.hours;
      const response = await getNetworkActivity(
        hours,
        itemsPerPage,
        workerId ? parseInt(workerId) : null,
        filterParams.startDate,
        filterParams.endDate
      );
      setActivities(response.data.data);
      setTotalCount(response.data.count || response.data.data.length);
    } catch (err) {
      console.error('Failed to load network data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (params) => {
    setFilterParams(params);
    setPage(1);
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    if (bytes < 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = Math.max(0, Math.min(i, sizes.length - 1));
    const value = bytes / Math.pow(k, size);
    const formatted = value.toFixed(2).replace(/\.?0+$/, '');
    return `${formatted} ${sizes[size]}`;
  };

  const getHourlyTraffic = () => {
    if (activities.length === 0) return [];
    let startTime, endTime;
    if (filterParams.startDate && filterParams.endDate) {
      startTime = dayjs(filterParams.startDate).toDate();
      endTime = dayjs(filterParams.endDate).toDate();
    } else if (filterParams.startDate) {
      startTime = dayjs(filterParams.startDate).toDate();
      endTime = new Date();
    } else {
      const timestamps = activities.map((a) => new Date(a.timestamp));
      startTime = new Date(Math.min(...timestamps));
      endTime = new Date(Math.max(...timestamps));
    }
    const hoursDiff = Math.ceil((endTime - startTime) / (1000 * 60 * 60));
    const numHours = Math.max(1, Math.min(168, hoursDiff));
    const hours = Array(numHours)
      .fill(0)
      .map((_, i) => {
        const hourTime = new Date(startTime.getTime() + i * 60 * 60 * 1000);
        return {
          hour: dayjs(hourTime).format('MMM DD HH:mm'),
          timestamp: hourTime,
          uploads: 0,
          count: 0,
          bytes_sent: 0,
          bytes_received: 0,
        };
      });
    activities.forEach((act) => {
      const actTime = new Date(act.timestamp);
      if (actTime < startTime || actTime > endTime) return;
      const hoursFromStart = Math.floor((actTime - startTime) / (1000 * 60 * 60));
      const hourIndex = Math.min(hoursFromStart, numHours - 1);
      if (hourIndex >= 0 && hourIndex < numHours) {
        hours[hourIndex].uploads += act.risk_score || 0;
        hours[hourIndex].count += 1;
        hours[hourIndex].bytes_sent += act.bytes_sent || 0;
        hours[hourIndex].bytes_received += act.bytes_received || 0;
      }
    });
    return hours;
  };

  const trafficData = getHourlyTraffic();
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Group activities by application (process_name) for the new view
  const groupedByApp = useMemo(() => {
    const groups = new Map();
    for (const a of activities) {
      const key = a.process_name || 'Unknown application';
      if (!groups.has(key)) {
        groups.set(key, {
          app: key,
          activities: [],
          bytes_sent: 0,
          bytes_received: 0,
          max_risk: 0,
          domains: new Set(),
          last_seen: 0,
        });
      }
      const g = groups.get(key);
      g.activities.push(a);
      g.bytes_sent += a.bytes_sent || 0;
      g.bytes_received += a.bytes_received || 0;
      if ((a.risk_score || 0) > g.max_risk) g.max_risk = a.risk_score || 0;
      if (a.domain) g.domains.add(a.domain);
      const ts = new Date(a.timestamp).getTime();
      if (ts > g.last_seen) g.last_seen = ts;
    }
    return Array.from(groups.values()).sort(
      (a, b) => b.bytes_sent + b.bytes_received - (a.bytes_sent + a.bytes_received)
    );
  }, [activities]);

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center text-sm text-muted-foreground">
        Loading...
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
      <PageHeader title="Network Traffic" description="Monitor network activity and detect data uploads" />

      <DateRangeFilter onFilterChange={handleFilterChange} />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Network Activity Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={trafficData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
              <YAxis
                tickFormatter={(value) => formatBytes(value)}
                label={{ value: 'Data Transfer', angle: -90, position: 'insideLeft' }}
              />
              <RTooltip
                formatter={(value, name) => {
                  if (
                    name === 'Data Sent' ||
                    name === 'Data Received' ||
                    name === 'bytes_sent' ||
                    name === 'bytes_received'
                  ) {
                    return [
                      formatBytes(value),
                      name === 'bytes_sent'
                        ? 'Data Sent'
                        : name === 'bytes_received'
                        ? 'Data Received'
                        : name,
                    ];
                  }
                  return [value, name];
                }}
              />
              <Legend />
              <Line type="monotone" dataKey="bytes_sent" stroke="#f44336" strokeWidth={2} name="Data Sent" />
              <Line
                type="monotone"
                dataKey="bytes_received"
                stroke="#4caf50"
                strokeWidth={2}
                name="Data Received"
              />
              <Line type="monotone" dataKey="count" stroke="#2196f3" strokeWidth={2} name="Activity Count" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Network Activity by Application</CardTitle>
        </CardHeader>
        <CardContent>
          {activities.length === 0 ? (
            <Alert variant="info">
              <AlertDescription>No network activity recorded yet</AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="mb-3 text-sm text-muted-foreground">
                Showing {activities.length} of {totalCount.toLocaleString()} activities across{' '}
                <span className="font-semibold text-foreground">{groupedByApp.length}</span>{' '}
                application{groupedByApp.length === 1 ? '' : 's'}
              </div>
              <ul className="flex max-h-[640px] flex-col gap-2.5 overflow-auto pr-1">
                {groupedByApp.map((group) => {
                  const expanded = !!expandedApps[group.app];
                  const initial = (group.app || '?').replace(/\.exe$/i, '').charAt(0).toUpperCase();
                  const totalBytes = group.bytes_sent + group.bytes_received;
                  return (
                    <li
                      key={group.app}
                      className={cn(
                        'group rounded-xl border bg-card transition-all',
                        expanded
                          ? 'border-primary/40 shadow-md'
                          : 'border-border hover:border-primary/30 hover:shadow-sm'
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => toggleApp(group.app)}
                        className="flex w-full items-center gap-4 px-4 py-3.5 text-left"
                      >
                        {/* Avatar */}
                        <div
                          className={cn(
                            'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-base font-semibold ring-1 transition-colors',
                            group.max_risk >= 30
                              ? 'bg-destructive/10 text-destructive ring-destructive/30'
                              : group.max_risk > 0
                              ? 'bg-warning/10 text-warning ring-warning/30'
                              : 'bg-primary/10 text-primary ring-primary/20'
                          )}
                        >
                          {initial}
                        </div>

                        {/* Title block */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-semibold text-foreground">
                              {group.app}
                            </span>
                            {group.max_risk > 0 && (
                              <Badge variant={group.max_risk >= 30 ? 'destructive' : 'warning'}>
                                Risk {group.max_risk}
                              </Badge>
                            )}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                            <span>
                              <span className="font-medium text-foreground tabular-nums">
                                {group.activities.length}
                              </span>{' '}
                              connection{group.activities.length === 1 ? '' : 's'}
                            </span>
                            <span aria-hidden>·</span>
                            <span>
                              <span className="font-medium text-foreground tabular-nums">
                                {group.domains.size}
                              </span>{' '}
                              domain{group.domains.size === 1 ? '' : 's'}
                            </span>
                            <span aria-hidden>·</span>
                            <span>Last {dayjs(group.last_seen).format('MMM DD, HH:mm')}</span>
                          </div>
                        </div>

                        {/* Right-side stats — single horizontal line so the
                            row stays compact and never clips */}
                        <div className="hidden shrink-0 items-center gap-3 sm:flex">
                          <span
                            className="inline-flex items-center gap-1 text-xs tabular-nums text-destructive"
                            title="Sent"
                          >
                            <CloudUpload className="h-3.5 w-3.5" />
                            {formatBytes(group.bytes_sent)}
                          </span>
                          <span
                            className="inline-flex items-center gap-1 text-xs tabular-nums text-success"
                            title="Received"
                          >
                            <CloudDownload className="h-3.5 w-3.5" />
                            {formatBytes(group.bytes_received)}
                          </span>
                          <span className="ml-1 rounded-md bg-muted px-2 py-1 text-xs font-semibold tabular-nums text-foreground">
                            {formatBytes(totalBytes)}
                          </span>
                        </div>

                        <ChevronRight
                          className={cn(
                            'h-5 w-5 shrink-0 text-muted-foreground transition-transform',
                            expanded && 'rotate-90 text-primary'
                          )}
                        />
                      </button>
                      {expanded && (
                        <ul className="divide-y divide-border overflow-hidden rounded-b-xl border-t border-border bg-muted/20">
                          {group.activities.map((activity) => (
                            <li key={activity.id} className="flex items-start gap-3 px-4 py-2.5 pl-11">
                              <div className="pt-0.5">
                                {activity.risk_score > 20 ? (
                                  <CloudUpload className="h-4 w-4 text-warning" />
                                ) : (
                                  <Globe className="h-4 w-4 text-primary" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="min-w-[200px] flex-1 truncate text-sm text-foreground">
                                    {activity.domain || activity.remote_address || 'Unknown'}
                                    {activity.remote_port ? `:${activity.remote_port}` : ''}
                                  </span>
                                  {activity.category && (
                                    <Badge variant="outline">{activity.category}</Badge>
                                  )}
                                  {activity.bytes_sent > 0 && (
                                    <Badge
                                      variant="outline"
                                      className="border-destructive/40 text-destructive"
                                    >
                                      <CloudUpload className="h-3 w-3" />↑{' '}
                                      {formatBytes(activity.bytes_sent)}
                                    </Badge>
                                  )}
                                  {activity.bytes_received > 0 && (
                                    <Badge
                                      variant="outline"
                                      className="border-success/40 text-success"
                                    >
                                      <CloudDownload className="h-3 w-3" />↓{' '}
                                      {formatBytes(activity.bytes_received)}
                                    </Badge>
                                  )}
                                  {activity.risk_score > 0 && (
                                    <Badge
                                      variant={
                                        activity.risk_score >= 30 ? 'destructive' : 'warning'
                                      }
                                    >
                                      Risk: {activity.risk_score}
                                    </Badge>
                                  )}
                                </div>
                                <div className="mt-0.5 text-xs text-muted-foreground">
                                  {dayjs(activity.timestamp).format('MMM DD, HH:mm:ss')}
                                  {activity.protocol ? ` • ${activity.protocol}` : ''}
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

export default NetworkTrafficPage;
